// Warehouse list for the sales screens. Lists active warehouses from the
// warehouse module (GET /v1/warehouses) — open to all authenticated roles.
import { useQuery } from "@tanstack/react-query";
import { apiGet } from "@/lib/api/client";

export interface Warehouse {
  id: string;
  name: string;
  address: string;
  isDefault: boolean;
  isActive: boolean;
}

export type SalesChannel = "retail" | "wholesale";

// Names of the seeded retail / wholesale warehouses (see migration
// 20260901000003_seed_retail_wholesale_warehouses). Used to pick the default
// kho per sales channel.
const RETAIL_WAREHOUSE_NAME = "Kho Bán Lẻ";
const WHOLESALE_WAREHOUSE_NAME = "Kho Bán Buôn";

/** Fetches active warehouses, cached for the session. */
export function useWarehouses() {
  return useQuery({
    queryKey: ["warehouses"],
    queryFn: () => apiGet<Warehouse[]>("/v1/warehouses"),
    staleTime: 300_000,
    select: (list) => (list ?? []).filter((w) => w.isActive),
  });
}

/** localStorage key holding the last-picked warehouse for a channel. */
export function warehouseStorageKey(channel: SalesChannel): string {
  return `sales_warehouse_${channel}`;
}

/** Default warehouse for a channel. Returns null if empty.
 *  - Bán lẻ  → "Kho Bán Lẻ", else kho mặc định, else kho đầu tiên.
 *  - Bán buôn → "Kho Bán Buôn" (nơi chứa hàng bán sỉ), else kho mặc định, else kho đầu tiên. */
export function defaultWarehouseId(
  warehouses: Warehouse[],
  channel: SalesChannel,
): string | null {
  if (warehouses.length === 0) return null;
  if (channel === "wholesale") {
    // Bán buôn xuất từ Kho Bán Buôn — nơi hàng bán sỉ thực sự được nhập/lưu.
    // (Trước đây mặc định Kho chính (isDefault) nhưng kho đó thường trống →
    // đơn bán sỉ không xuất được hàng.)
    return (
      warehouses.find((w) => w.name === WHOLESALE_WAREHOUSE_NAME)?.id ??
      warehouses.find((w) => w.isDefault)?.id ??
      warehouses[0].id
    );
  }
  // Bán lẻ xuất từ Kho Bán Lẻ.
  return (
    warehouses.find((w) => w.name === RETAIL_WAREHOUSE_NAME)?.id ??
    warehouses.find((w) => w.isDefault)?.id ??
    warehouses[0].id
  );
}

// Bumped when the default-warehouse rules change, to drop stale per-channel
// choices saved in localStorage (else an old default keeps overriding the new one).
const WAREHOUSE_PREF_VERSION = "wh-v2-buon-default";

/** Resolves the initial warehouse for a channel: a still-valid localStorage
 *  choice wins, otherwise the channel default. */
export function initialWarehouseId(
  warehouses: Warehouse[],
  channel: SalesChannel,
): string | null {
  if (warehouses.length === 0) return null;
  try {
    if (localStorage.getItem("sales_warehouse_pref_ver") === WAREHOUSE_PREF_VERSION) {
      const saved = localStorage.getItem(warehouseStorageKey(channel));
      if (saved && warehouses.some((w) => w.id === saved)) return saved;
    } else {
      // One-time reset when the default rules change: drop stale per-channel
      // choices so the new defaults take effect, then stamp the current version.
      localStorage.removeItem(warehouseStorageKey("retail"));
      localStorage.removeItem(warehouseStorageKey("wholesale"));
      localStorage.setItem("sales_warehouse_pref_ver", WAREHOUSE_PREF_VERSION);
    }
  } catch {
    // ignore storage errors
  }
  return defaultWarehouseId(warehouses, channel);
}
